import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./Blog.css";

const BlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: "",
    category: "",
    hint: "",
    author: "",
    description: "",
    image: null,
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    fetchCategories();
    if (isEdit && id) {
      fetchBlog();
    }
  }, [id, isEdit]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        "https://ambatturindustrialestate.com/api/api/categories/"
      );
      if (!response.ok) throw new Error("Failed to fetch categories");

      const data = await response.json();
      console.log("Categories API Response:", data); // Debug log
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError("Failed to load categories");
    }
  };

  const fetchBlog = async () => {
    if (!id) {
      setError("Invalid blog ID");
      return;
    }

    try {
      const token = localStorage.getItem("access");
      const response = await fetch(
        `https://ambatturindustrialestate.com/api/api/blogs/${id}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok)
        throw new Error(`Failed to fetch blog: ${response.status}`);

      const blog = await response.json();
      console.log("Blog API Response:", blog); // Debug log

      setFormData({
        title: blog.title || "",
        category: blog.category || blog.category_id || "",
        hint: blog.hint || "",
        author: blog.author || "",
        description: blog.description || "",
        image: null,
      });

      if (blog.image) {
        setImagePreview(blog.image);
      }
    } catch (err) {
      console.error("Error fetching blog:", err);
      setError(err.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("access");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const formDataToSend = new FormData();

      // Append all required fields
      formDataToSend.append("title", formData.title);
      formDataToSend.append("category", formData.category);
      if (formData.hint) formDataToSend.append("hint", formData.hint);
      formDataToSend.append("author", formData.author);
      formDataToSend.append("description", formData.description);

      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      const url = isEdit
        ? `https://ambatturindustrialestate.com/api/api/blogs/${id}/`
        : "https://ambatturindustrialestate.com/api/api/blogs/create/";

      const method = isEdit ? "PUT" : "POST";

      console.log("Sending request to:", url); // Debug log
      console.log("Form data:", Object.fromEntries(formDataToSend)); // Debug log

      const response = await fetch(url, {
        method: method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      const responseData = await response.json();
      console.log("Response:", responseData); // Debug log

      if (!response.ok) {
        throw new Error(
          responseData.detail ||
            responseData.message ||
            `HTTP error! status: ${response.status}`
        );
      }

      navigate("/blogs");
    } catch (err) {
      console.error("Error saving blog:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="blog-form-container">
      <div className="blog-form-header">
        <h1>{isEdit ? "Edit Blog" : "Create New Blog"}</h1>
        <button onClick={() => navigate("/blogs")} className="blog-back-btn">
          Back to Blogs
        </button>
      </div>

      {error && (
        <div className="blog-error">
          <strong>Error:</strong> {error}
          <button onClick={() => setError(null)} className="error-close">
            ×
          </button>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="blog-form"
        encType="multipart/form-data"
      >
        <div className="form-group">
          <label htmlFor="title">Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="category">Category *</label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat.category_id} value={cat.category_id}>
                {cat.name} (ID: {cat.category_id})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="hint">Hint</label>
          <input
            type="text"
            id="hint"
            name="hint"
            value={formData.hint}
            onChange={handleChange}
            disabled={loading}
            placeholder="Optional hint for the blog"
          />
        </div>

        <div className="form-group">
          <label htmlFor="author">Author *</label>
          <input
            type="text"
            id="author"
            name="author"
            value={formData.author}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description *</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="6"
            required
            disabled={loading}
            placeholder="Write your blog content here..."
          />
        </div>

        <div className="form-group">
          <label htmlFor="image">Image</label>
          <input
            type="file"
            id="image"
            name="image"
            accept="image/*"
            onChange={handleImageChange}
            disabled={loading}
          />
          {imagePreview && (
            <div className="image-preview">
              <img src={imagePreview} alt="Preview" />
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading} className="blog-submit-btn">
            {loading ? "Saving..." : isEdit ? "Update Blog" : "Create Blog"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/blogs")}
            className="blog-cancel-btn"
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;
